import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSubscription } from '../../hooks/useSubscription';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { DashboardHeader } from '../../components/dashboard/DashboardHeader';
import type { Profile, TeamRole } from '../../types';
import { 
    UserPlus, 
    Trash2, 
    UserCircle, 
    CreditCard, 
    MoreVertical, 
    Pencil, 
    AlertCircle,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuLabel, 
    DropdownMenuSeparator, 
    DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';
import { InviteMemberModal } from '../../components/modals/InviteMemberModal';
import { ConfirmationModal } from '../../components/modals/ConfirmationModal';

interface Organization {
  id: string;
  name: string;
  licenses_purchased: number;
  licenses_used: number;
}

interface Member extends Profile {
  member_id: string;
  role: 'admin' | 'member';
  analysis_count: number;
  joined_at?: string;
}

interface MemberListItemProps {
  member: Member;
  currentUserId: string | undefined;
  onRoleChange: (memberUserId: string, newRole: TeamRole) => Promise<void>;
  onRemoveMember: (memberUserId: string, memberEmail: string) => Promise<void> | void;
}

function MemberListItem({ 
    member, 
    currentUserId, 
    onRoleChange, 
    onRemoveMember 
}: MemberListItemProps) {
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const isSelf = member.id === currentUserId;
  const usageLimit = 1000; 
  const usagePercentage = Math.min((member.analysis_count / usageLimit) * 100, 100);

  const handleRoleChange = async (newRole: TeamRole) => {
    if (member.role === newRole) return;
    setIsChangingRole(true);
    setActionError(null);
    try {
      await onRoleChange(member.id, newRole);
    } catch (err: any) {
      setActionError(err.message || 'Failed to change role');
    } finally {
      setIsChangingRole(false);
    }
  };

  const handleRemoveClick = () => {
    onRemoveMember(member.id, member.email ?? 'this member');
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between py-4 px-4 hover:bg-muted/50 transition-colors duration-150 gap-4">
      <div className="flex items-center space-x-4 flex-1 min-w-0">
        <Avatar className="h-10 w-10">
            <AvatarImage src={member.avatar_url ?? undefined} alt={member.full_name ?? 'avatar'} />
            <AvatarFallback>
                <UserCircle className="h-6 w-6 text-muted-foreground" />
            </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-grow space-y-1.5">
          <div>
            <p className="text-sm font-medium text-foreground truncate">
                {member.full_name || 'Invited User'}
                {isSelf ? <span className="text-xs text-muted-foreground"> (You)</span> : ''}
            </p>
            <p className="text-xs text-muted-foreground truncate">{member.email}</p>
          </div>
          <div className="w-full max-w-xs"> 
              <div className="flex justify-between text-xs text-muted-foreground mb-1">
                 <span>Usage</span>
                 <span>{member.analysis_count} / {usageLimit}</span>
              </div>
              <Progress 
                  value={usagePercentage} 
                  className={cn("h-2 w-full", {
                      '[&>div]:bg-red-600': usagePercentage > 90,
                      '[&>div]:bg-yellow-500': usagePercentage > 75 && usagePercentage <= 90,
                      '[&>div]:bg-blue-600': usagePercentage <= 75
                  })}
              />
          </div>
          {actionError && <p className="text-xs text-red-600 mt-1">Error: {actionError}</p>}
        </div>
      </div>
      <div className="flex items-center space-x-3 flex-shrink-0 self-end sm:self-center">
        <span className="text-sm text-muted-foreground hidden md:inline">
          {member.joined_at ? `Joined: ${new Date(member.joined_at).toLocaleDateString()}` : <span className='italic'>Pending Invite</span>}
        </span>
        
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button 
                    variant="outline" 
                    size="sm"
                    className="w-[100px] justify-between capitalize" 
                    disabled={isChangingRole || isSelf}
                >
                    {isChangingRole ? 'Changing...' : member.role}
                    {!isSelf && <MoreVertical className="ml-1 h-4 w-4 text-muted-foreground" />}
                </Button>
            </DropdownMenuTrigger>
            {!isSelf && (
                 <DropdownMenuContent align="end" className="w-36">
                    <DropdownMenuLabel>Change Role</DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                        onClick={() => handleRoleChange('admin')} 
                        disabled={member.role === 'admin' || isChangingRole}
                    >
                        Admin
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                        onClick={() => handleRoleChange('member')} 
                        disabled={member.role === 'member' || isChangingRole}
                    >
                        Member
                    </DropdownMenuItem>
                </DropdownMenuContent>
            )}
        </DropdownMenu>

         {!isSelf && (
             <Button 
                variant="ghost" 
                size="icon"
                onClick={handleRemoveClick} 
                title="Remove Member"
                className="text-muted-foreground hover:text-red-600 hover:bg-red-100 dark:hover:bg-red-900/20"
                disabled={isChangingRole}
              >
                 <Trash2 className="h-4 w-4"/>
             </Button>
          )}
      </div>
    </div>
  );
}

export function OrganizationSettings() {
  const { user } = useAuth();
  const { 
    isLoading: subLoading, 
    error: subError,
    organization: userOrganization,
    organizationRole, 
    refetch: refetchSubscription 
  } = useSubscription();
  
  const navigate = useNavigate();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isRedirectingToPortal, setIsRedirectingToPortal] = useState(false);
  const [portalError, setPortalError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [memberToRemove, setMemberToRemove] = useState<{ id: string; email: string } | null>(null);
  
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const organizationId = userOrganization?.organizationId;

  useEffect(() => {
    if (!subLoading) {
      if (!organizationId || organizationRole !== 'admin') {
        console.warn("Access denied: User is not an organization admin.");
        navigate('/dashboard');
      }
    }
  }, [subLoading, organizationId, organizationRole, navigate]);

  useEffect(() => {
    if (organizationId && organizationRole === 'admin') {
      const fetchData = async () => {
        setIsLoadingData(true);
        setError(null);
        setInviteError(null);
        setPortalError(null);
        setActionError(null);
        setNameError(null);
        try {
          const { data: orgData, error: orgError } = await supabase
            .from('organizations')
            .select('id, name, licenses_purchased, licenses_used')
            .eq('id', organizationId)
            .maybeSingle();

          if (orgError) throw new Error(`Failed to fetch organization details: ${orgError.message}`);
          setOrganization(orgData);

          const { data: memberData, error: memberError } = await supabase
            .from('organization_members')
            .select(`
              id,
              role,
              analysis_count,
              joined_at,
              profiles (
                id,
                email,
                full_name,
                avatar_url
              )
            `)
            .eq('organization_id', organizationId);
            
          if (memberError) throw new Error(`Failed to fetch members: ${memberError.message}`);
          
          const mappedMembers: Member[] = memberData?.map((m: any) => ({
            member_id: m.id,
            role: m.role,
            analysis_count: m.analysis_count,
            joined_at: m.joined_at,
            id: m.profiles?.id ?? '', 
            email: m.profiles?.email ?? 'N/A',
            full_name: m.profiles?.full_name ?? 'Invited User',
            avatar_url: m.profiles?.avatar_url,
            organization_id: organizationId,
          })) || [];
          setMembers(mappedMembers);

        } catch (err: any) {
          console.error("Error loading organization settings:", err);
          setError(err.message || "Failed to load organization data.");
        } finally {
          setIsLoadingData(false);
        }
      };
      fetchData();
    } else if (!subLoading) {
      setIsLoadingData(false);
    }
  }, [organizationId, organizationRole, subLoading]);

  useEffect(() => {
      if (organization) {
          setNewName(organization.name);
      }
  }, [organization]);

  const handleInviteSubmit = async (email: string, role: TeamRole) => {
    setInviteError(null);
    if (!organizationId) throw new Error("Organization ID is missing");
    console.log(`Calling invite function for ${email} as ${role} to org ${organizationId}`);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke('api-invite-team-members', {
        body: { organizationId, email, role },
      });
      if (invokeError) throw new Error(invokeError.message || "Failed to communicate with invite service.");
      if (data?.error) throw new Error(data.error);
      console.log("Invite successful:", data);
      setIsInviteModalOpen(false);
      await refetchSubscription(); 
      await refetchMembers();
      alert(data.message || `Invite successfully sent to ${email}!`);
    } catch (err: any) {
       console.error("Error inviting member:", err);
       setInviteError(err.message || 'An unexpected error occurred during invitation.');
       throw err;
    }
  };

  const handleManageBilling = async () => {
    setPortalError(null);
    setIsRedirectingToPortal(true);

    if (!organizationId) {
        setPortalError("Organization ID not found.");
        setIsRedirectingToPortal(false);
        return;
    }

    console.log("Requesting Stripe Billing Portal session...");

    try {
       const { data, error: invokeError } = await supabase.functions.invoke('api-billing-portal', {
         body: { organizationId: organizationId },
       });

       if (invokeError) throw new Error(invokeError.message || "Failed to communicate with billing service.");
       if (data?.error) throw new Error(data.error);
       if (!data?.url) throw new Error("Billing portal URL was not returned.");

       console.log("Redirecting to Stripe Portal...");
       window.location.href = data.url; 

    } catch (err: any) {
        console.error("Error creating Stripe Billing Portal session:", err);
        setPortalError(err.message || "Could not open billing portal. Please try again.");
        setIsRedirectingToPortal(false);
    }

  };

  const refetchMembers = async () => {
      if (!organizationId) return;
      console.log("Refetching members...");
      setActionError(null);
      try {
            const { data: memberData, error: memberError } = await supabase
                .from('organization_members')
                .select(`id, role, analysis_count, joined_at, profiles (id, email, full_name, avatar_url)`)
                .eq('organization_id', organizationId);
                
            if (memberError) throw memberError;
            
            const mappedMembers: Member[] = memberData?.map((m: any) => ({
                member_id: m.id,
                role: m.role,
                analysis_count: m.analysis_count,
                joined_at: m.joined_at,
                id: m.profiles?.id ?? '', 
                email: m.profiles?.email ?? 'N/A',
                full_name: m.profiles?.full_name ?? 'Invited User',
                avatar_url: m.profiles?.avatar_url,
                organization_id: organizationId,
            })) || [];
            setMembers(mappedMembers);
      } catch (err: any) {
          console.error("Failed to refetch members:", err);
          setActionError("Failed to refresh member list after action.");
      }
  };

  const handleRoleChangeSubmit = async (memberUserId: string, newRole: TeamRole) => {
    setActionError(null);
    if (!organizationId) throw new Error("Organization ID missing");

    console.log(`Calling update role function for ${memberUserId} to ${newRole}`);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('api-update-member-role', {
        body: { organizationId, memberUserId, newRole },
      });
      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);
      
      console.log("Role update successful:", data);
      await refetchMembers();
      
    } catch (err: any) {
      console.error("Error changing member role:", err);
      throw err;
    }
  };

  const openRemoveConfirmation = (memberUserId: string, memberEmail: string) => {
      setMemberToRemove({ id: memberUserId, email: memberEmail });
      setShowConfirmModal(true);
  };

  const handleRemoveSubmit = async () => {
    if (!memberToRemove || !organizationId) return;
    setActionError(null);
    setShowConfirmModal(false);

    console.log(`Calling remove function for ${memberToRemove.id}`);
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('api-remove-member', {
        body: { organizationId, memberUserId: memberToRemove.id },
      });
      if (invokeError) throw invokeError;
      if (data?.error) throw new Error(data.error);

      console.log("Remove member successful:", data);
      setMemberToRemove(null);
      await refetchMembers();
      await refetchSubscription();

    } catch (err: any) {
        console.error("Error removing member:", err);
        setActionError(err.message || 'Failed to remove member.');
        setMemberToRemove(null);
    }
  };

  const handleUpdateOrgName = async () => {
      if (!organizationId || !newName || newName.trim() === organization?.name) {
          setIsEditingName(false);
          setNameError(null);
          return;
      }
      setIsSavingName(true);
      setNameError(null);

      try {
          const { data, error: invokeError } = await supabase.functions.invoke('api-update-org-name', {
              body: { organizationId, newName: newName.trim() },
          });

          if (invokeError) throw invokeError;
          if (data?.error) throw new Error(data.error);

          console.log("Org name update successful:", data);
          setOrganization(prev => prev ? { ...prev, name: data.updatedOrganization.name } : null);
          setIsEditingName(false);

      } catch (err: any) {
          console.error("Error updating organization name:", err);
          setNameError(err.message || 'Failed to update name.');
      } finally {
          setIsSavingName(false);
      }
  };

  const licensesAvailable = 100 - (organization?.licenses_used ?? 0);

  if (isLoadingData || subLoading) {
    return (
      <div className="min-h-screen w-full bg-slate-50">
        <DashboardHeader />
        <div className="w-full px-6 lg:px-8 xl:px-12 py-8 flex justify-center items-center min-h-[400px]">
         <div className="flex flex-col items-center space-y-2">
             <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                 <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                 <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
             </svg>
             <p className="text-muted-foreground">Loading Organization Settings...</p>
         </div>
       </div>
      </div>
    );
  }

  if (error || subError) {
     return (
       <div className="min-h-screen w-full bg-slate-50">
         <DashboardHeader />
         <div className="w-full px-6 lg:px-8 xl:px-12 py-8 max-w-3xl">
           <Alert variant="destructive">
             <AlertCircle className="h-4 w-4" />
             <AlertTitle>Error Loading Settings</AlertTitle>
             <AlertDescription>
               {error || subError || 'An unexpected error occurred while loading organization settings. Please try refreshing the page.'}
             </AlertDescription>
           </Alert>
         </div>
       </div>
     );
  }
  
  if (!organizationId || organizationRole !== 'admin') {
     return (
       <div className="min-h-screen w-full bg-slate-50">
         <DashboardHeader />
         <div className="w-full px-6 lg:px-8 xl:px-12 py-8 max-w-3xl">
            <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Access Denied</AlertTitle>
                <AlertDescription>
                  You do not have permission to manage organization settings.
                </AlertDescription>
            </Alert>
         </div>
       </div>
     );
  }
  
  return (
    <div className="min-h-screen w-full bg-slate-50">
      <DashboardHeader />
      <div className="w-full px-6 lg:px-8 xl:px-12 py-8 max-w-4xl space-y-8">
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Organization Settings</h1>
        
        <div className="space-y-4">
             {inviteError && (
                 <Alert variant="destructive">
                     <AlertCircle className="h-4 w-4" />
                     <AlertTitle>Invite Error</AlertTitle>
                     <AlertDescription>{inviteError}</AlertDescription>
                 </Alert>
             )}
             {portalError && (
                 <Alert variant="destructive">
                     <AlertCircle className="h-4 w-4" />
                     <AlertTitle>Billing Error</AlertTitle>
                     <AlertDescription>{portalError}</AlertDescription>
                 </Alert>
             )}
             {actionError && (
                 <Alert variant="destructive">
                     <AlertCircle className="h-4 w-4" />
                     <AlertTitle>Action Error</AlertTitle>
                     <AlertDescription>{actionError}</AlertDescription>
                 </Alert>
             )}
             {nameError && (
                  <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertTitle>Name Update Error</AlertTitle>
                      <AlertDescription>{nameError}</AlertDescription>
                  </Alert>
              )}
         </div>

        <Card>
          <CardHeader>
              <div className="flex justify-between items-center">
                  <CardTitle>Organization Details</CardTitle>
                  {!isEditingName && (
                      <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => { setIsEditingName(true); setNameError(null); setNewName(organization?.name || ''); }}
                      >
                          <Pencil className="mr-2 h-4 w-4" />
                          Edit Name
                      </Button>
                  )}
              </div>
              <CardDescription>Manage your organization's name.</CardDescription>
          </CardHeader>
          <CardContent>
              {isEditingName ? (
                  <div className="space-y-4">
                      <div className="space-y-2">
                          <Label htmlFor="org-name">Organization Name</Label>
                          <Input 
                              id="org-name"
                              value={newName}
                              onChange={(e) => setNewName(e.target.value)}
                              disabled={isSavingName}
                              maxLength={100}
                              placeholder="Enter organization name"
                          />
                      </div>
                      <div className="flex justify-end space-x-2">
                           <Button variant="ghost" onClick={() => setIsEditingName(false)} disabled={isSavingName}>
                               Cancel
                           </Button>
                           <Button onClick={handleUpdateOrgName} disabled={isSavingName || !newName.trim() || newName.trim() === organization?.name}>
                               {isSavingName ? 'Saving...' : 'Save Changes'}
                           </Button>
                      </div>
                  </div>
              ) : (
                  <p className="text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">Name:</span> {organization?.name || 'N/A'}
                  </p>
              )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Billing & Subscription</CardTitle>
            <CardDescription>Enterprise plan with 100 licenses included.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
               <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Current Plan</span>
                  <span className="font-medium text-foreground">Enterprise</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Licenses Used</span>
                  <span className="font-medium text-foreground">{organization?.licenses_used ?? 'N/A'} / 100</span>
              </div>
              <div className="space-y-2 pt-2">
                  <div className="space-y-2">
                    <Label>License Usage</Label>
                    <Progress 
                      value={organization ? (organization.licenses_used / 100) * 100 : 0}
                      className="w-full"
                    />
                    <p className="text-xs text-muted-foreground">
                      {organization ? `${organization.licenses_used} of 100 licenses used` : 'Loading...'}
                    </p>
                  </div>
                  <div className="pt-2">
                    <p className="text-sm text-muted-foreground">
                      Your enterprise plan includes 100 user licenses. Each license allows 1,000 analyses per month.
                    </p>
                  </div>
               </div>
          </CardContent>
          <CardFooter className="border-t pt-6">
               <Button
                  variant="outline"
                  onClick={handleManageBilling}
                  disabled={isRedirectingToPortal}
               >
                  <CreditCard className="mr-2 h-4 w-4" />
                  {isRedirectingToPortal ? 'Redirecting...' : 'Manage Payment & Invoices'}
              </Button>
          </CardFooter>
        </Card>

        <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                  <div>
                       <CardTitle>Members</CardTitle>
                       <CardDescription>
                          Manage who has access to your organization. ({members.length} / 100 licenses used)
                       </CardDescription>
                  </div>
                  <Button 
                      onClick={() => setIsInviteModalOpen(true)} 
                      disabled={licensesAvailable <= 0} 
                      title={licensesAvailable <= 0 ? "No available licenses to invite" : "Invite a new member"}
                  >
                      <UserPlus className="mr-2 h-4 w-4" />
                      Invite Member
                  </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {members.length > 0 ? (
                  <div className="divide-y divide-border">
                      {members.map(member => (
                          <MemberListItem 
                              key={member.member_id} 
                              member={member} 
                              currentUserId={user?.id} 
                              onRoleChange={handleRoleChangeSubmit}
                              onRemoveMember={openRemoveConfirmation} 
                          />
                      ))}
                  </div>
              ) : (
                  <p className="text-muted-foreground text-center py-6 px-6">No members found. Invite your team!</p>
              )}
            </CardContent>
        </Card>

        <InviteMemberModal 
          isOpen={isInviteModalOpen} 
          onClose={() => setIsInviteModalOpen(false)}
          onSubmit={handleInviteSubmit}
          seatsAvailable={licensesAvailable}
        />

        {memberToRemove && (
             <ConfirmationModal
                 isOpen={showConfirmModal}
                 onClose={() => { setShowConfirmModal(false); setMemberToRemove(null); }}
                 onConfirm={handleRemoveSubmit}
                 title="Remove Member"
                 message={`Are you sure you want to remove ${memberToRemove.email} from the organization? This action cannot be undone.`}
                 confirmText="Remove"
                 isDestructive={true}
             />
         )}

      </div>
    </div>
  );
} 